/* iter-p2-c — Studio Health pill.
 *
 * Small header badge that fetches `/api/studio-dashboard/me` and
 * renders a weighted (throughput × voice × prompt-completion) score
 * with a colour tone. Clicking opens the Studio Production Dashboard.
 * Only mounts for users who have the Bulk Novel capability (Premier).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Factory, Gauge, Mic, ClipboardList } from 'lucide-react';
import { openStudioDashboard } from '@/components/StudioProductionDashboard';
import { useCapabilities } from '@/lib/useCapabilities';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

/* Weighted score: throughput (0-100) × 40% + voice (0-100) × 40% +
 * prompt completion (0-100) × 20%. Zero components are treated as
 * "no data yet" and skipped from the weighting so a fresh account
 * doesn't score a false zero. */
function computeHealth(data) {
  if (!data) return { score: 0, hasData: false };
  const bulk    = data.bulk_throughput    || {};
  const voice   = data.voice_consistency  || {};
  const prompt  = data.prompt_completion  || {};
  const throughputSignal = Math.min(100, (bulk.completed_novels || 0) * 25);   // 4 novels → 100
  const voiceSignal      = voice.sample_size ? (voice.avg_score || 0) : null;
  const promptSignal     = prompt.total_prompts ? (prompt.overall_rate || 0) : null;
  const parts = [];
  if (bulk.completed_novels || bulk.runs)     parts.push({ w: 0.40, v: throughputSignal });
  if (voiceSignal !== null)                    parts.push({ w: 0.40, v: voiceSignal });
  if (promptSignal !== null)                   parts.push({ w: 0.20, v: promptSignal });
  if (!parts.length) return { score: 0, hasData: false };
  const totalW = parts.reduce((s, p) => s + p.w, 0);
  const score  = Math.round(parts.reduce((s, p) => s + p.v * p.w, 0) / totalW);
  return { score, hasData: true, breakdown: { throughputSignal, voiceSignal, promptSignal } };
}

export default function StudioHealthPill() {
  const caps = useCapabilities();
  const isPremier = !!caps?.bulk_draft_novel;
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!isPremier) return undefined;
    let alive = true;
    const load = () => {
      axios.get(`${API}/studio-dashboard/me`)
        .then((r) => { if (alive) setData(r.data); })
        .catch(() => { /* silent — pill just hides */ });
    };
    load();
    // Refresh every 90s so the pill stays roughly warm.
    const iv = setInterval(load, 90_000);
    return () => { alive = false; clearInterval(iv); };
  }, [isPremier]);

  if (!isPremier) return null;
  const { score, hasData, breakdown } = computeHealth(data);
  if (!hasData) return null;

  const tone = score >= 80 ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200'
    : score >= 60 ? 'border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-200'
    : 'border-rose-500/50 bg-rose-500/10 text-rose-800 dark:text-rose-200';

  const fmt = (v) => (v == null ? '—' : `${Math.round(v)}/100`);

  return (
    <div className="relative group inline-block" data-testid="studio-health-pill-wrap">
      <button
        type="button"
        onClick={openStudioDashboard}
        className={
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold '
          + 'hover:opacity-90 transition-opacity ' + tone
        }
        data-testid="studio-health-pill"
        data-score={score}
        title="Hover for the breakdown, click for the full dashboard."
      >
        <Factory className="h-3 w-3" />
        Studio · {score}/100
      </button>
      {/* iter-p2-d — Hover drill-down: component scores + weight badges.
          Purely CSS-driven (group-hover) so no state churn per hover. */}
      <div
        className="pointer-events-none absolute right-0 top-full z-[60] mt-1 w-64 origin-top-right scale-95 rounded-md border border-border bg-card p-3 opacity-0 shadow-xl transition-all duration-150 group-hover:pointer-events-auto group-hover:scale-100 group-hover:opacity-100"
        data-testid="studio-health-drilldown"
      >
        <div className="mb-1.5 flex items-center justify-between text-[10.5px] uppercase tracking-wider text-muted-foreground">
          <span>Studio health · breakdown</span>
          <span className="rounded-sm bg-secondary px-1.5 py-0.5 font-mono-stat text-[10px] text-foreground">{score}/100</span>
        </div>
        <ul className="space-y-1 text-[11.5px]" data-testid="studio-health-drilldown-list">
          <li className="flex items-center justify-between gap-2" data-testid="studio-health-row-throughput">
            <span className="flex items-center gap-1.5"><Gauge className="h-3 w-3 text-primary" /> Throughput</span>
            <span className="font-mono-stat">
              {fmt(breakdown?.throughputSignal)}
              <span className="ml-1 text-[9px] text-muted-foreground">×40%</span>
            </span>
          </li>
          <li className="flex items-center justify-between gap-2" data-testid="studio-health-row-voice">
            <span className="flex items-center gap-1.5"><Mic className="h-3 w-3 text-primary" /> Voice consistency</span>
            <span className="font-mono-stat">
              {fmt(breakdown?.voiceSignal)}
              <span className="ml-1 text-[9px] text-muted-foreground">×40%</span>
            </span>
          </li>
          <li className="flex items-center justify-between gap-2" data-testid="studio-health-row-prompt">
            <span className="flex items-center gap-1.5"><ClipboardList className="h-3 w-3 text-primary" /> Prompt completion</span>
            <span className="font-mono-stat">
              {fmt(breakdown?.promptSignal)}
              <span className="ml-1 text-[9px] text-muted-foreground">×20%</span>
            </span>
          </li>
        </ul>
        <div className="mt-2 text-right text-[10px] text-muted-foreground">
          Click the pill for the full dashboard.
        </div>
      </div>
    </div>
  );
}
